// File: StarHistoryView.tsx
import React, { useState, useMemo } from 'react';
import { usePage, router } from '@inertiajs/react';
import { Container, Paper, Text, Group, Drawer, Stack, Box, SimpleGrid, Tabs, Image } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconHeart, IconCalendar, IconCurrencyDollar } from '@tabler/icons-react';
import dayjs from 'dayjs';
import artistStarIcon from '../../assets/images/money-star-icon.png';

interface StarPurchase {
  id: number;
  stars: number;
  message: string | null;
  created_at: string;
  amount: number; // Changed from amount_cents
  stripe_transaction_id?: string;
  artist?: { id: number; name: string };
  supporter?: { id: number; name: string };
  supporter_name?: { id: number; name: string };
  user?: { id: number; username: string; name: string }; // Added user as alternative
}

interface Props {
  purchases: StarPurchase[];
  viewType: 'purchased' | 'received';
  selectedRange: string;
}

const StarHistoryView: React.FC<Props> = ({ purchases, viewType, selectedRange }) => {
  //   console.log('⭐⭐⭐⭐⭐ StarHistoryView purchases:', purchases);

  if (!purchases || !Array.isArray(purchases)) {
    return (
      <Container p="lg" style={{ marginBottom: '1rem', backgroundColor: '#17171790' }}>
        <Text c="white">You haven't received any stars yet!</Text>
      </Container>
    );
  }

  const [activeItem, setActiveItem] = useState<StarPurchase | null>(null);
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Calculate metrics using stars * $5
  const metrics = useMemo(() => {
    const totalCount = purchases.length;

    const thirtyDaysAgo = dayjs().subtract(30, 'days');
    const last30DaysAmount = purchases
      .filter((purchase) => dayjs(purchase.created_at).isAfter(thirtyDaysAgo))
      .reduce((total, purchase) => total + purchase.stars * 5, 0);

    const allTimeAmount = purchases.reduce((total, purchase) => total + purchase.stars * 5, 0);

    return {
      totalCount,
      last30DaysAmount,
      allTimeAmount,
    };
  }, [purchases]);

  const StatCard = ({ icon, value, label }: { icon: React.ReactNode; value: string | number; label: string }) => (
    <Paper p="md" radius="lg" style={{ backgroundColor: '#17171790', textAlign: 'center' }}>
      <Stack gap="4px" align="center">
        <Text size="2rem" fw={700} c="white" lh={1}>
          {value}
        </Text>
        <Group gap="4px" justify="center">
          {icon}
          <Text size="12px" c="dimmed">
            {label}
          </Text>
        </Group>
      </Stack>
    </Paper>
  );

  const labels = {
    countLabel: viewType === 'purchased' ? 'Artists Stars' : 'Supporters',
    transactionsHeader: viewType === 'purchased' ? 'Recent Purchases' : 'Recent Transactions',
  };

  return (
    <Container p="lg" style={{ marginBottom: '1rem', backgroundColor: '#75757590' }}>
      <Text fw={700} size="2rem" c="white" mb="md" className="rubik-header mb-4">
        {viewType === 'received' ? 'Supporters' : 'Your Star Purchases'}
      </Text>

      {/* Tabs */}
      <Tabs defaultValue="one-time" mb="xl">
        <Tabs.List>
          <Tabs.Tab value="one-time" c="white">
            One-time
          </Tabs.Tab>
          <Tabs.Tab value="settings" c="white">
            Settings
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="one-time" pt="xl">
          {/* Stats Cards */}
          <SimpleGrid cols={isMobile ? 1 : 3} spacing="xs" mb="2rem">
            <StatCard
              icon={<IconHeart size={20} color="#666" />}
              value={metrics.totalCount}
              label={labels.countLabel}
            />
            <StatCard
              icon={<IconCalendar size={20} color="#666" />}
              value={`$${metrics.last30DaysAmount.toFixed(0)}`}
              label="Last 30 days"
            />
            <StatCard
              icon={<IconCurrencyDollar size={20} color="#666" />}
              value={`$${metrics.allTimeAmount.toFixed(0)}`}
              label="All-time"
            />
          </SimpleGrid>

          {/* Recent transactions header */}
          <Text fw={700} c="white" mb="md" className="poppins mb-2" style={{ fontSize: '1.2rem' }}>
            Recent Transactions
          </Text>

          {/* Transaction List */}
          <Stack>
            {purchases.map((purchase) => (
              <Paper
                key={purchase.id}
                shadow="xs"
                radius="md"
                p="md"
                onClick={() => setActiveItem(purchase)}
                style={{ backgroundColor: '#17171790', cursor: 'pointer' }}
              >
                <Group justify="space-between" wrap="nowrap" align="center">
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Group gap="xs" align="center" wrap="nowrap">
                      <Image src={artistStarIcon} alt="Stars" className="star-icon" />

                      <Text
                        c="#f2f2f2"
                        fw={500}
                        style={{
                          lineHeight: 1.2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          letterSpacing: '-0.5px',
                          fontSize: '0.8rem',
                        }}
                      >
                        {viewType === 'received'
                          ? typeof purchase.supporter_name === 'string'
                            ? purchase.supporter_name
                            : purchase.supporter_name?.name || purchase.user?.name
                          : purchase.artist?.name}
                      </Text>
                    </Group>
                  </Box>
                  <Box ta="right" style={{ flexShrink: 0 }}>
                    <Text c="#f2f2f2" fw={500} style={{ lineHeight: 1.2 }}>
                      ${(purchase.stars * 5).toFixed(2)}
                    </Text>
                    <Text size="xs" c="dimmed" style={{ lineHeight: 1.2 }}>
                      {dayjs(purchase.created_at).format('MMM D, YYYY')}
                    </Text>
                  </Box>
                </Group>
              </Paper>
            ))}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="settings" pt="xl">
          <Text c="white">Settings panel - Coming soon</Text>
        </Tabs.Panel>
      </Tabs>

      {/* Transaction Details Drawer */}
      <Drawer
        opened={!!activeItem}
        onClose={() => setActiveItem(null)}
        title="Star Purchase Details"
        position="right"
        size="sm"
        styles={{
          header: { backgroundColor: '#17171790', color: 'white', fontWeight: 700 },
          body: { backgroundColor: '#171717', color: 'white', minHeight: '100vh' },
        }}
      >
        {activeItem && (
          <Box className="pt-3">
            <Text fw={700} c="orange" size="xl">
              Name:
            </Text>
            <Text className="mb-3" size="lg">
              {viewType === 'received'
                ? typeof activeItem.supporter_name === 'string'
                  ? activeItem.supporter_name
                  : activeItem.supporter_name?.name || activeItem.user?.name
                : activeItem.artist?.name}
            </Text>

            <Text fw={700} c="orange" size="xl">
              Stars:
            </Text>
            <Text className="mb-3" size="lg">
              {activeItem.stars} ⭐ (${(activeItem.stars * 5).toFixed(2)})
            </Text>

            <Text fw={700} c="orange" size="xl">
              Message:
            </Text>
            <Text className="mb-3" size="lg">
              {activeItem.message || '—'}
            </Text>

            <Text fw={700} c="orange" size="xl">
              Amount Paid (with fees):
            </Text>
            <Text className="mb-3" size="lg">
              ${(activeItem.amount / 100).toFixed(2)}
            </Text>

            {activeItem.stripe_transaction_id && (
              <>
                <Text fw={700} c="orange" size="xl">
                  Stripe Transaction ID:
                </Text>
                <Text className="mb-3" size="lg">
                  {activeItem.stripe_transaction_id}
                </Text>
              </>
            )}

            <Text fw={700} c="orange" size="xl">
              Date:
            </Text>
            <Text className="mb-3" size="lg">
              {dayjs(activeItem.created_at).format('MMMM D, YYYY')}
            </Text>
          </Box>
        )}
      </Drawer>
    </Container>
  );
};

export default StarHistoryView;
